import { i, x } from './lit-element-DJ-EMK-H.js';
import { getWishlists, removeWishlistItem } from './wishlist-api.js';
import { isGuest } from './wishlist-utils.js';
import { spinnerIcon } from './wishlist-icons.js';
import './wishlist-image-grid.js';
import './wishlist-mark-as-bought-modal.js';
import './wishlist-modal-component.js';
import './wishlist-product-card-mini.js';
import './wishlist-product-card.js';
import './wishlist-navigation.js';
import './wishlist-header.js';
import loginPrompt from './wishlist-login-prompt-component.js';
import './wishlist-share-component.js';
import './wishlist-new-list-component.js';

/**
 * Wishlist Products Component
 * Displays products from a specific wishlist
 */
class WishlistProductsComponent extends i {
  static properties = {
    wishlist: { type: Object },
    allWishlists: { type: Array },
    selectedWishlistId: { type: String },
    loading: { type: Boolean },
    error: { type: String },
    cartLoading: { type: Object },
    cartSuccess: { type: Object },
    cartError: { type: Object },
    isSavingDate: { type: Boolean },
    confirmingRemoveVariantId: { type: String, state: true },
    isPublicView: { type: Boolean },
    isGuest: { type: Boolean },
    autoLoad: { type: Boolean, attribute: 'auto-load' },
    onWishlistUpdated: { type: Function, attribute: false },
    onWishlistDeleted: { type: Function, attribute: false },
    onBackToWishlists: { type: Function, attribute: false },
    onWishlistCreated: { type: Function, attribute: false },
    onWishlistSelected: { type: Function, attribute: false },
    onProductRemoved: { type: Function, attribute: false },
    onCartUpdated: { type: Function, attribute: false },
    shareModalOpen: { type: Boolean, state: true },
    editModalOpen: { type: Boolean, state: true },
    createModalOpen: { type: Boolean, state: true },
  };

  #messageTimeout = null;
  #handleProductBought = (event) => {
    const { wishlistId, variantId, boughtBy } = event.detail;

    if (this.wishlist && this.wishlist.id === wishlistId && this.wishlist.items) {
      this.wishlist = {
        ...this.wishlist,
        items: this.wishlist.items.map((p) =>
          p.variant_id === variantId ? { ...p, bought_by: boughtBy } : p
        ),
      };
      this.requestUpdate();
    }
  };

  #handleCartUpdated = (event) => {
    if (event.detail?.result && typeof this.onCartUpdated === 'function') {
      this.onCartUpdated(event.detail.result);
    }
  };

  #handleWishlistRefresh = async () => {
    if (!this.wishlist?.id) return;

    this.loading = true;
    this.requestUpdate();

    try {
      const wishlists = await getWishlists(true);
      if (wishlists?.length > 0) {
        const sortedWishlists = [...wishlists].sort((a, b) => a.name.localeCompare(b.name));
        this.allWishlists = sortedWishlists;

        const updatedWishlist = sortedWishlists.find((w) => w.id === this.wishlist.id);
        if (updatedWishlist) {
          this.wishlist = updatedWishlist;
        }
      }
    } catch (error) {
      console.error('Error refreshing wishlist:', error);
      this.error =
        window.wishlistTranslations?.errors?.refreshFailed ||
        'Failed to refresh wishlist. Please try again.';
    } finally {
      this.loading = false;
      this.requestUpdate();
    }
  };

  #handleWishlistShareUpdate = (updatedWishlist) => {
    if (updatedWishlist) {
      this.wishlist = {
        ...this.wishlist,
        share_url: updatedWishlist.share_url,
        shared: updatedWishlist.shared,
      };
      this.onWishlistUpdated(this.wishlist);
      this.requestUpdate();
    }
  };

  #handleWishlistUpdate = (updatedWishlist) => {
    if (this.wishlist && updatedWishlist && this.wishlist.id === updatedWishlist.id) {
      const items = this.wishlist.items || [];

      const preservedData = {};
      for (const field of ['market_id', 'language_iso_2', 'domain']) {
        if (this.wishlist[field] && !updatedWishlist[field]) {
          preservedData[field] = this.wishlist[field];
        }
      }

      this.wishlist = { ...this.wishlist, ...updatedWishlist, ...preservedData, items };

      if (this.allWishlists?.length > 0) {
        this.allWishlists = this.allWishlists.map((w) =>
          w.id === updatedWishlist.id ? { ...w, ...updatedWishlist, ...preservedData } : w
        );
      }

      this.requestUpdate();
      this.onWishlistUpdated(updatedWishlist);
      this.editModalOpen = false;
    }
  };

  #handleWishlistDelete = (wishlistId) => {
    this.allWishlists = this.allWishlists.filter((w) => w.id !== wishlistId);

    if (this.wishlist?.id === wishlistId) {
      if (this.allWishlists.length > 0) {
        this.wishlist = this.allWishlists[0];
        this.selectedWishlistId = this.allWishlists[0].id;
      } else {
        this.wishlist = null;
        this.selectedWishlistId = null;
      }
    }

    this.requestUpdate();
    this.onWishlistDeleted(wishlistId);
    this.onBackToWishlists();
    this.editModalOpen = false;
  };

  #handleWishlistCreate = (wishlist) => {
    if (wishlist) {
      this.allWishlists = [...this.allWishlists, wishlist];
      this.wishlist = wishlist;
      this.selectedWishlistId = wishlist.id;
      this.onWishlistCreated(wishlist);
      this.createModalOpen = false;
    }
  };

  constructor() {
    super();
    this.wishlist = null;
    this.allWishlists = [];
    this.selectedWishlistId = null;
    this.loading = false;
    this.error = null;
    this.cartLoading = {};
    this.cartSuccess = {};
    this.cartError = {};
    this.isSavingDate = false;
    this.confirmingRemoveVariantId = null;
    this.isPublicView = false;
    this.isGuest = isGuest();
    this.shareModalOpen = false;
    this.editModalOpen = false;
    this.createModalOpen = false;
    this.autoLoad = false;

    this.onWishlistUpdated = () => {};
    this.onWishlistDeleted = () => {};
    this.onBackToWishlists = () => {};
    this.onWishlistCreated = () => {};
    this.onWishlistSelected = () => {};
    this.onProductRemoved = () => {};
    this.onCartUpdated = () => {};

    document.querySelectorAll('login-prompt-component').forEach((el) => {
      el.remove();
    });
  }

  connectedCallback() {
    super.connectedCallback();

    // Listen for product bought events
    document.addEventListener('product-bought', this.#handleProductBought);
    document.addEventListener('cart-updated', this.#handleCartUpdated);
    document.addEventListener('wishlist-refresh', this.#handleWishlistRefresh);

    // Auto-load wishlists if the attribute is set
    if (this.autoLoad) {
      this.loadWishlists();
    }

    if (!this.querySelector('mark-as-bought-modal')) {
      const modal = document.createElement('mark-as-bought-modal');
      this.appendChild(modal);
    }

    this.#checkGuestStatus();
  }

  /** Check if user is a guest */
  #checkGuestStatus() {
    this.isGuest = isGuest();
    this.requestUpdate();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('product-bought', this.#handleProductBought);
    document.removeEventListener('cart-updated', this.#handleCartUpdated);
    document.removeEventListener('wishlist-refresh', this.#handleWishlistRefresh);

    if (this.#messageTimeout) {
      clearTimeout(this.#messageTimeout);
    }
  }

  updated(changedProperties) {
    if (changedProperties.has('wishlist') && this.wishlist) {
      this.selectedWishlistId = this.wishlist.id;
    }

    if (
      changedProperties.has('allWishlists') &&
      this.allWishlists?.length > 0 &&
      !this.selectedWishlistId
    ) {
      this.wishlist = this.allWishlists[0];
      this.selectedWishlistId = this.allWishlists[0].id;
    }
  }

  showTransientMessage(message, type = 'success') {
    if (type === 'success') {
      window.toast.success(message);
    } else {
      window.toast.error(message);
    }
  }

  async addToCart(product) {
    if (!product || !product.variant_id) {
      window.toast.error(
        window.wishlistTranslations?.errors?.productDetailsIncomplete ||
          'Product details incomplete. Cannot add to cart.'
      );
      return;
    }

    const variantId = product.variant_id;

    this.cartLoading = { ...this.cartLoading, [variantId]: true };
    this.cartError = { ...this.cartError, [variantId]: null };
    this.cartSuccess = { ...this.cartSuccess, [variantId]: false };
    this.requestUpdate();

    try {
      const formData = new FormData();
      const variantId = String(product.variant_id).trim();

      if (!variantId || variantId === 'undefined' || variantId === 'null') {
        throw new Error('Invalid variant ID format');
      }

      formData.append('id', variantId);
      formData.append('quantity', 1);

      if (this.wishlist?.name) {
        formData.append('properties[_wishlist_name]', this.wishlist.name);
      }

      if (this.wishlist?.id) {
        formData.append('properties[_wishlist_id]', this.wishlist.id);
      }

      const cartDrawer = document.querySelector('cart-drawer');
      const cartNotification = document.querySelector('cart-notification');

      let sectionsToFetch = [];

      if (cartNotification) {
        sectionsToFetch = [
          ...cartNotification.getSectionsToRender().map((section) => section.section),
        ];
      }

      const cartItems = document.querySelector('cart-items');

      if (cartItems) {
        sectionsToFetch = [
          ...sectionsToFetch,
          ...cartItems.getSectionsToRender().map((section) => section.section),
        ];
        sectionsToFetch = [...new Set(sectionsToFetch)];
      }

      if (sectionsToFetch.length > 0) {
        formData.append('sections', sectionsToFetch);
        formData.append('sections_url', window.location.pathname);
      }

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData,
        headers: {
          Accept: 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to add to cart: ${response.statusText}`);
      }

      const result = await response.json();

      this.cartSuccess = { ...this.cartSuccess, [variantId]: true };
      this.requestUpdate();
      setTimeout(() => {
        this.cartSuccess = { ...this.cartSuccess, [variantId]: false };
        this.requestUpdate();
      }, 2000);

      this.onCartUpdated(result);

      if (cartDrawer) {
        cartDrawer.renderContents(result);
        if (cartItems) {
          cartItems.classList.remove('cart-is-empty');
        }
        setTimeout(() => {
          cartDrawer.open();
        });
      } else if (cartNotification) {
        cartNotification.setActiveElement(document.activeElement);
        cartNotification.renderContents(result);
      }

      return result;
    } catch (error) {
      console.error('Error adding to cart:', error);
      this.cartError = {
        ...this.cartError,
        [variantId]: error.message || window.wishlistTranslations?.errors?.addToCart,
      };
      this.requestUpdate();

      setTimeout(() => {
        this.cartError = { ...this.cartError, [variantId]: null };
        this.requestUpdate();
      }, 3000);

      return null;
    } finally {
      this.cartLoading = { ...this.cartLoading, [variantId]: false };
      this.requestUpdate();
    }
  }

  /**
   * Update cart icon count
   * @param {Number} count - New cart count
   */
  #updateCartIcon(count) {
    if (typeof count !== 'number') return;

    const bubbleCounter = document.querySelector('.cart-count-bubble');
    if (bubbleCounter) {
      bubbleCounter.textContent = count;

      // Toggle visibility based on count
      if (count > 0) {
        bubbleCounter.classList.remove('hidden');
      } else {
        bubbleCounter.classList.add('hidden');
      }
    }
  }

  async removeFromWishlist(variantId) {
    if (!this.wishlist || !variantId || !this.wishlist.items) return;

    const itemToRemove = this.wishlist.items.find((item) => item.variant_id === variantId);

    if (!itemToRemove?.variant_id) {
      const message =
        window.wishlistTranslations?.errors?.failedToFindProductDetailsForRemoval ||
        'Failed to find product details for removal.';
      console.error(message, variantId);
      window.toast.error(message);
      return;
    }

    this.loading = true;
    this.error = null;

    try {
      await removeWishlistItem(this.wishlist.id, itemToRemove.variant_id);
      this.onProductRemoved(this.wishlist.id, itemToRemove.variant_id);

      this.wishlist = {
        ...this.wishlist,
        items: this.wishlist.items.filter((p) => p.variant_id !== variantId),
      };
      this.showTransientMessage(window.wishlistTranslations?.success?.productRemoved, 'success');
    } catch (error) {
      console.error('Failed to remove item from wishlist', error);
      this.showTransientMessage(window.wishlistTranslations?.errors?.removeFromWishlist, 'error');
    } finally {
      this.loading = false;
      this.requestUpdate();
    }
  }

  openBoughtModal = (product) => {
    if (!this.wishlist || !product) return;

    let modal = this.querySelector('mark-as-bought-modal');
    if (!modal) {
      modal = document.createElement('mark-as-bought-modal');
      this.appendChild(modal);
    }

    requestAnimationFrame(() => {
      modal.open(
        product,
        this.wishlist.id,
        this.isPublicView,
        this.isPublicView && this.wishlist.share_slug ? this.wishlist.share_slug : null
      );
    });
  };

  openShareModal = () => {
    if (this.isGuest) {
      loginPrompt.show();
      return;
    }
    this.shareModalOpen = true;
  };

  openEditModal = () => {
    if (this.isGuest) {
      loginPrompt.show();
      return;
    }
    this.editModalOpen = true;
  };

  showCreateNewWishlist = () => {
    if (this.isGuest) {
      loginPrompt.show();
      return;
    }
    this.createModalOpen = true;
  };

  handleWishlistNavigationClick = (wishlistId) => {
    if (wishlistId && wishlistId !== this.selectedWishlistId) {
      const selected = this.allWishlists.find((w) => w.id === wishlistId);
      if (selected) {
        this.wishlist = selected;
        this.selectedWishlistId = wishlistId;
        this.onWishlistSelected(wishlistId);
      }
    }
  };

  #renderLoadingState() {
    return x`
      <div class="flex items-center justify-center p-8">
        ${spinnerIcon('h-8 w-8 animate-spin text-primary')}
      </div>
    `;
  }

  #renderErrorState() {
    return x`
      <div class="mb-4 rounded border-l-4 border-[#e53e3e] bg-[#fff5f5] p-4">
        <p class="text-[#9b2c2c]">${this.error}</p>
      </div>
    `;
  }

  #renderGuestNotice() {
    return x`
      <div class="mb-6 hidden rounded-md bg-gray-50 p-4">
        <div class="flex">
          <div class="flex-shrink-0">
            <svg class="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
              <path
                fill-rule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clip-rule="evenodd"
              />
            </svg>
          </div>
          <div class="ml-3 flex-1 md:flex md:justify-between">
            <p class="text-sm text-gray-700">
              ${window.wishlistTranslations?.messages?.guestWishlistNotice ||
              "You're using a guest wishlist."}
              <a href="/account/login" class="font-medium text-gray-700 underline"
                >${window.wishlistTranslations?.buttons?.logIn || 'Log in'}</a
              >
              ${window.wishlistTranslations?.messages?.or || 'or'}
              <a href="/account/register" class="font-medium text-gray-700 underline"
                >${window.wishlistTranslations?.buttons?.createAccount || 'create an account'}</a
              >
              ${window.wishlistTranslations?.messages?.accessAllFeatures ||
              'to access all features.'}
            </p>
          </div>
        </div>
      </div>
    `;
  }

  #renderEmptyWishlist() {
    return x`
      <div class="rounded-lg bg-gray-50 py-8 text-center">
        <p class="mb-4 text-gray-500">${window.wishlistTranslations?.messages?.noProducts}</p>
        ${this.isPublicView
          ? ''
          : x`
              <button
                @click=${() => (window.location.href = '/collections/all')}
                class="button button--conversion"
              >
                ${window.wishlistTranslations?.buttons?.addProducts}
              </button>
            `}
      </div>
    `;
  }

  #renderWishlistProducts() {
    return x`
      <div
        class="wishlist-products ${this.isPublicView
          ? 'lg:grid-cols-4'
          : 'lg:grid-cols-3'} grid grid-cols-2 gap-0 max-md:-mx-6"
      >
        ${this.wishlist.items.map(
          (product) => x`
            <wishlist-product-card
              .product=${product}
              .wishlistId=${this.wishlist.id}
              .wishlistName=${this.wishlist.name}
              .cartLoading=${this.cartLoading[product.variant_id] === true}
              .cartSuccess=${this.cartSuccess[product.variant_id] === true}
              .cartError=${this.cartError[product.variant_id]}
              .isPublicView=${this.isPublicView}
              .isGuest=${this.isGuest}
              .confirmingRemoveVariantId=${this.confirmingRemoveVariantId === product.variant_id
                ? product.variant_id
                : null}
              .onAddToCart=${this.addToCart.bind(this)}
              .onOpenBoughtModal=${this.openBoughtModal}
              .onRemoveFromWishlist=${this.removeFromWishlist.bind(this)}
            ></wishlist-product-card>
          `
        )}
      </div>
    `;
  }

  #renderShareModalContent() {
    return x`
      <wishlist-share-component
        .wishlist=${this.wishlist}
        .onWishlistUpdated=${this.#handleWishlistShareUpdate}
        .onShareComplete=${this.#handleShareComplete}
      ></wishlist-share-component>
    `;
  }

  #renderEditModalContent() {
    return x`
      <wishlist-new-list-component
        .mode=${'update'}
        .wishlistId=${this.wishlist?.id}
        .wishlistToEdit=${this.wishlist}
        .navigationSource=${'update'}
        .onWishlistUpdated=${this.#handleWishlistUpdate}
        .onWishlistDeleted=${this.#handleWishlistDelete}
      ></wishlist-new-list-component>
    `;
  }

  #renderCreateModalContent() {
    return x`
      <wishlist-new-list-component
        .navigationSource=${'products'}
        .onWishlistCreated=${this.#handleWishlistCreate}
      ></wishlist-new-list-component>
    `;
  }

  render() {
    if (this.loading && !this.wishlist) {
      return this.#renderLoadingState();
    }

    if (this.error && !this.wishlist) {
      return this.#renderErrorState();
    }

    if (!this.wishlist) {
      return x`<p>${window.wishlistTranslations?.messages?.noLists}</p>`;
    }

    return x`
      ${!this.isPublicView
        ? x`
            <h1 class="mb-4 font-bold text-xl md:text-2xl" id="wishlist-heading">
              ${window.wishlistTranslations?.myWishlists}
            </h1>
            ${this.isGuest ? this.#renderGuestNotice() : ''}
          `
        : ''}

      <div class="md:grid md:grid-cols-12 md:gap-8" id="wishlist-products-container">
        ${this.isPublicView
          ? ''
          : x`
              <aside
                class="sticky -top-4 z-20 mb-6 h-auto bg-white pt-2 md:col-span-3 md:mb-0 md:self-start lg:top-8 lg:pt-0"
              >
                <wishlist-navigation
                  .wishlists=${this.allWishlists}
                  .selectedWishlistId=${this.selectedWishlistId}
                  .onWishlistSelected=${this.handleWishlistNavigationClick}
                  .onCreateNewWishlist=${this.showCreateNewWishlist}
                  .isGuest=${this.isGuest}
                ></wishlist-navigation>
              </aside>
            `}

        <div class="${this.isPublicView ? 'w-full md:col-span-12' : 'md:col-span-9'}">
          <wishlist-header
            .wishlist=${this.wishlist}
            .isPublicView=${this.isPublicView}
            .isGuest=${this.isGuest}
            .onEdit=${this.openEditModal}
            .onShare=${this.openShareModal}
          ></wishlist-header>

          ${!this.wishlist.items?.length
            ? this.#renderEmptyWishlist()
            : this.#renderWishlistProducts()}
        </div>
      </div>

      <!-- Share Modal -->
      <modal-component
        class="share-modal"
        .showCloseButton=${true}
        .isOpen=${this.shareModalOpen}
        .title=${window.wishlistTranslations?.modals?.shareWishlist}
        .content=${this.#renderShareModalContent()}
        @modal-closed=${this.#handleShareModalClose}
      ></modal-component>

      <!-- Edit Modal -->
      <modal-component
        class="edit-modal"
        .showCloseButton=${true}
        .isOpen=${this.editModalOpen}
        .title=${window.wishlistTranslations?.modals?.editWishlist}
        .content=${this.#renderEditModalContent()}
        @modal-closed=${this.#handleEditModalClose}
      ></modal-component>

      <!-- Create Modal -->
      <modal-component
        class="create-modal"
        .showCloseButton=${true}
        .isOpen=${this.createModalOpen}
        .title=${window.wishlistTranslations?.modals?.createNew}
        .content=${this.#renderCreateModalContent()}
        @modal-closed=${this.#handleCreateModalClose}
      ></modal-component>
    `;
  }

  createRenderRoot() {
    return this;
  }

  #handleShareModalClose = () => {
    this.shareModalOpen = false;
  };

  #handleEditModalClose = () => {
    this.editModalOpen = false;
  };

  #handleCreateModalClose = () => {
    this.createModalOpen = false;
  };

  #handleShareComplete = (message, type) => {
    this.showTransientMessage(message, type);
    this.shareModalOpen = false;
  };

  async loadWishlists() {
    if (this.loading) return;

    try {
      this.loading = true;
      this.requestUpdate();

      const wishlists = await getWishlists(true);

      if (wishlists && wishlists.length > 0) {
        const sortedWishlists = [...wishlists].sort((a, b) => a.name.localeCompare(b.name));

        this.allWishlists = sortedWishlists;

        this.wishlist = sortedWishlists[0];
        this.selectedWishlistId = sortedWishlists[0].id;
      }
    } catch (error) {
      console.error('Error loading wishlists:', error);
      this.error = 'Failed to load your wishlists. Please try again.';
    } finally {
      this.loading = false;
      this.requestUpdate();
    }
  }
}

if (!customElements.get('wishlist-products-component')) {
  customElements.define('wishlist-products-component', WishlistProductsComponent);
}

export { WishlistProductsComponent };
